import { supabase } from "../../../../supabase";

/**
 * Uploads an avatar image to Supabase storage
 * @param {File} file - The avatar file to upload
 * @param {string} userId - The user ID to associate with the avatar
 * @returns {Promise<string|null>} The public URL of the uploaded avatar or null if upload failed
 */
export async function uploadAvatar(file, userId) {
  if (!file || !userId) {
    console.log("❌ [uploadAvatar] Missing file or userId:", {
      hasFile: !!file,
      userId,
    });
    return null;
  }

  try {
    // Generate a unique file name
    const fileExt = file.name.split(".").pop().toLowerCase();
    const randomId = Math.random().toString(36).substring(2);
    const fileName = `${randomId}.${fileExt}`;

    // Store in user-specific folder
    const filePath = `${userId}/${fileName}`;

    // Upload file to the avatars bucket
    const { data, error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: true,
      });

    if (uploadError) {
      console.error("❌ [uploadAvatar] Upload error:", uploadError);
      throw uploadError;
    }

    console.log("✅ [uploadAvatar] Upload successful:", data);

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("avatars").getPublicUrl(filePath);

    console.log("🔗 [uploadAvatar] Generated public URL:", publicUrl);

    return publicUrl;
  } catch (error) {
    console.error("❌ [uploadAvatar] Error uploading avatar:", error);
    return null;
  }
}

/**
 * Validates an avatar file
 * @param {File} file - The avatar file to validate
 * @returns {Object} Validation result with isValid and error properties
 */
export function validateAvatarFile(file) {
  if (!file) {
    return { isValid: false, error: "No file provided" };
  }

  // Validate file type
  const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
  if (!validTypes.includes(file.type)) {
    return {
      isValid: false,
      error: "Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.",
    };
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    return {
      isValid: false,
      error: "File too large. Please upload an image smaller than 5MB.",
    };
  }

  return { isValid: true, error: null };
}

export default {
  uploadAvatar,
  validateAvatarFile,
};
